import { useStore } from '@/state/store';
import { parseFold, foldToPattern, serializeFold } from '@/io/fold';
import { patternToSvg, svgToPattern } from '@/io/svg';
import { patternToObj } from '@/io/obj';
import { meshToStl } from '@/io/stl';
import { buildSimulationMesh } from '@/simulation/mesh';
import { downloadBuffer, downloadText, pickFile, slug } from '@/io/files';
import { encodePatternToUrl } from '@/share/url';

/** File-menu behaviour, kept out of the React tree so it stays testable. */
export async function openFoldFile(): Promise<void> {
  const state = useStore.getState();
  const file = await pickFile('.fold,application/json');
  if (!file) return;
  const { file: parsed, errors } = parseFold(file.text);
  for (const e of errors) state.notify('warning', e);
  if (!parsed) {
    state.notify('error', 'That file could not be read as FOLD.');
    return;
  }
  const { pattern, issues } = foldToPattern(parsed);
  for (const issue of issues) state.notify(issue.severity === 'error' ? 'error' : 'warning', issue.message);
  if (!pattern) {
    state.notify('error', 'The FOLD file has no usable crease pattern.');
    return;
  }
  pattern.meta.title = pattern.meta.title ?? file.name.replace(/\.fold$/i, '');
  state.loadPattern(pattern, `Open ${file.name}`);
}

export async function importSvgFile(): Promise<void> {
  const state = useStore.getState();
  const file = await pickFile('.svg,image/svg+xml');
  if (!file) return;
  const { pattern, report } = svgToPattern(file.text);
  if (report.segments === 0) {
    state.notify('error', 'No straight-line segments were found in that SVG.');
    return;
  }
  for (const s of report.skipped) state.notify('warning', s);
  if (report.unmapped.length > 0) {
    state.notify(
      'warning',
      `${report.unmapped.length} stroke colour(s) did not match a crease type (${report.unmapped
        .slice(0, 4)
        .join(', ')}). Those creases were imported as unassigned — assign them with the M/V tools.`,
    );
  }
  pattern.meta.title = file.name.replace(/\.svg$/i, '');
  state.loadPattern(pattern, `Import ${file.name}`);
}

export function saveFold(): void {
  const state = useStore.getState();
  const frame = state.frame;
  const mesh = buildSimulationMesh(state.pattern).mesh;
  // Fold the mesh positions back onto pattern vertices for the folded frame.
  let folded: Float32Array | null = null;
  if (frame && frame.positions.length === mesh.vertexCount * 3) {
    folded = new Float32Array(state.pattern.verticesCoords.length * 3);
    for (let v = 0; v < mesh.vertexCount; v++) {
      const pv = mesh.vertexPatternIndex[v];
      folded[pv * 3] = frame.positions[v * 3];
      folded[pv * 3 + 1] = frame.positions[v * 3 + 1];
      folded[pv * 3 + 2] = frame.positions[v * 3 + 2];
    }
  }
  const text = serializeFold(state.pattern, {
    foldedPositions: folded,
    measuredFoldAngles: frame?.edgeFoldAngles ?? null,
  });
  downloadText(`${slug(state.pattern.meta.title ?? 'origami')}.fold`, text);
}

export function exportSvg(): void {
  const state = useStore.getState();
  downloadText(
    `${slug(state.pattern.meta.title ?? 'origami')}-cp.svg`,
    patternToSvg(state.pattern, { includeAuxiliary: state.view.showAuxiliary }),
    'image/svg+xml',
  );
}

export function exportObj(): void {
  const state = useStore.getState();
  const mesh = buildSimulationMesh(state.pattern).mesh;
  const positions =
    state.frame && state.frame.positions.length === mesh.vertexCount * 3
      ? state.frame.positions
      : Float32Array.from(mesh.restPositions);
  downloadText(
    `${slug(state.pattern.meta.title ?? 'origami')}.obj`,
    patternToObj(mesh, positions, slug(state.pattern.meta.title ?? 'origami')),
    'text/plain',
  );
}

export function exportStl(): void {
  const state = useStore.getState();
  const mesh = buildSimulationMesh(state.pattern).mesh;
  const positions =
    state.frame && state.frame.positions.length === mesh.vertexCount * 3
      ? state.frame.positions
      : Float32Array.from(mesh.restPositions);
  downloadBuffer(
    `${slug(state.pattern.meta.title ?? 'origami')}.stl`,
    meshToStl(mesh, positions),
    'model/stl',
  );
  state.notify(
    'info',
    'STL has no notion of a two-sided surface, so the export is a zero-thickness shell. Slicers will report it as non-manifold.',
  );
}

export function saveScreenshot(): void {
  const state = useStore.getState();
  const canvases = Array.from(document.querySelectorAll('canvas'));
  const canvas = canvases[canvases.length - 1] as HTMLCanvasElement | undefined;
  if (!canvas) return;
  canvas.toBlob((blob) => {
    if (!blob) {
      state.notify('error', 'The 3D view could not be captured.');
      return;
    }
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${slug(state.pattern.meta.title ?? 'origami')}.png`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  });
}

export async function copyShareLink(): Promise<void> {
  const state = useStore.getState();
  const { url, error } = await encodePatternToUrl(state.pattern);
  if (error || !url) {
    state.notify('warning', error ?? 'Could not encode this pattern.');
    return;
  }
  try {
    await navigator.clipboard.writeText(url);
    state.notify('info', 'Shareable link copied to the clipboard.');
  } catch {
    state.notify('info', url);
  }
}
